import React, { useCallback } from 'react'
import styled from 'styled-components'
import palette from 'core-system/Themes/palette'

const pwdMeterColor = {
  Weak: palette.error.red500,
  'So-so': palette.warning.yellow500,
  Good: palette.accent.teal400,
  Great: palette.accent.teal600,
}

const PwdMeterContainer = styled.div<{ password: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0.2rem 0;
  margin-bottom: 1rem;
  visibility: ${(props) => (props.password ? 'visible' : 'hidden')};
`

const PwdMeterScore = styled.progress<{ color: string }>`
  appearance: none;
  -webkit-appearance: none;
  margin-right: 0.5rem;
  height: 0.5rem;
  flex: 1;
  border-radius: 0.75rem;

  &::-webkit-progress-bar {
    background-color: #eee;
    border-radius: 0.75rem;
  }

  &::-webkit-progress-value {
    background-color: ${(props) => props.color};
  }

  &::-webkit-progress-value {
    border-radius: 0.75rem;
  }

  &::-moz-progress-bar {
    background-color: ${(props) => props.color};
    border-radius: 0.75rem;
  }
`

const PwdMeterTitle = styled.label<{ color: string }>`
  width: 2.5rem;
  color: ${(props) => props.color};
  font-weight: 700;
`

export const pwdScore = (password: string) => {
  //eslint-disable-next-line
  const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/
  const noNumber = /\d/
  if (password.length < 5) {
    return 0
  } else if (
    password.length >= 15 &&
    specialChars.test(password) &&
    noNumber.test(password)
  ) {
    return 4
  } else if (
    password.length >= 10 &&
    specialChars.test(password) &&
    noNumber.test(password)
  ) {
    return 3
  } else if (
    password.length >= 10 &&
    (specialChars.test(password) || noNumber.test(password))
  ) {
    return 2
  } else {
    return 0
  }
}

interface PasswordStrengthMeterProps {
  password: string
}

const PasswordStrengthMeter = (props: PasswordStrengthMeterProps) => {
  const { password } = props

  const createPasswordLabel = useCallback((result: number): string => {
    switch (result) {
      case 0:
        return 'Weak'
      case 1:
        return 'Weak'
      case 2:
        return 'So-so'
      case 3:
        return 'Good'
      case 4:
        return 'Great'
      default:
        return 'Weak'
    }
  }, [])

  const pwdTested = pwdScore(password)

  const pwdLabel = createPasswordLabel(pwdTested)

  return (
    <PwdMeterContainer password={password}>
      <PwdMeterScore
        value={pwdTested}
        max={4}
        color={pwdMeterColor[pwdLabel]}
      />
      <PwdMeterTitle color={pwdMeterColor[pwdLabel]}>{pwdLabel}</PwdMeterTitle>
    </PwdMeterContainer>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  PasswordStrengthMeter.displayName = 'PasswordStrengthMeter'
}

export default PasswordStrengthMeter
